import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useRef } from 'react';
import BookDemo from './BookDemo';

const floatingLights = keyframes`
  0%, 100% {
    opacity: 0;
    transform: translate(0, 0) scale(1);
  }
  33% {
    opacity: 0.4;
    transform: translate(30px, -20px) scale(1.2);
  }
  66% {
    opacity: 0.2;
    transform: translate(-20px, 10px) scale(0.8);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(20px);
  }
`;

const CoverContainer = styled.div`
  color: white;
  padding: 6rem 2rem;
  text-align: center;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  position: relative;
  overflow: hidden;
  background: black;

  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
    min-height: 50vh;
  }

  &::before, &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
  }

  &::before {
    background: 
      radial-gradient(circle at 20% 30%, rgba(98, 255, 182, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(72, 187, 255, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    animation: ${floatingLights} 8s infinite alternate;
  }

  &::after {
    background: 
      radial-gradient(circle at 70% 20%, rgba(98, 255, 182, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 30% 80%, rgba(72, 187, 255, 0.05) 0%, transparent 50%),
      radial-gradient(circle at 60% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    animation: ${floatingLights} 12s infinite alternate-reverse;
    animation-delay: -3s;
  }
`;

const VideoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
`;

const VideoBackground = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (max-width: 768px) {
    width: 100%;
    height: auto;
    max-height: 100%;
  }
`;

const TextOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.5s ease;
  z-index: 2;
  pointer-events: none;
`;

const ContentWrapper = styled.div<{ isVisible: boolean }>`
  opacity: ${props => props.isVisible ? 1 : 0};
  animation: ${props => props.isVisible ? fadeIn : fadeOut} 0.5s ease-out forwards;
  z-index: 3;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const VideoControls = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  z-index: 4;
  display: flex;
  gap: 10px;
`;

const ControlButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  backdrop-filter: blur(5px);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Title = styled.h1`
  font-size: 3.5rem;
  margin: 0 0 1rem;
  font-weight: 700;
  max-width: 800px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.5rem;
  margin: 0 0 2rem;
  opacity: 0.9;
  max-width: 600px;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: #48bb78;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.125rem;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 480px) {
    padding: 0.875rem 2rem;
    font-size: 1rem;
  }

  &:hover {
    background: #38a169;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
`;

interface CoverProps {
  title: string;
  subtitle: string;
  ctaText: string;
}

const Cover = ({ title, subtitle, ctaText }: CoverProps) => {
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = async () => {
    const doc = document as Document;
    const container = containerRef.current as HTMLElement | null;

    if (!doc.fullscreenElement && 
        !doc.webkitFullscreenElement && 
        !doc.mozFullScreenElement &&
        !doc.msFullscreenElement) {
      try {
        if (container) {
          const elementToFullscreen = videoRef.current || container;
          
          if (elementToFullscreen.requestFullscreen) {
            await elementToFullscreen.requestFullscreen();
          } else if (elementToFullscreen.webkitRequestFullscreen) {
            await elementToFullscreen.webkitRequestFullscreen();
          } else if (elementToFullscreen.mozRequestFullScreen) {
            await elementToFullscreen.mozRequestFullScreen();
          } else if (elementToFullscreen.msRequestFullscreen) {
            await elementToFullscreen.msRequestFullscreen();
          }
          setIsFullscreen(true);
        }
      } catch (err) {
        console.error('Error attempting to enable fullscreen:', err);
      }
    } else {
      try {
        if (doc.exitFullscreen) {
          await doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          await doc.webkitExitFullscreen();
        } else if (doc.mozCancelFullScreen) {
          await doc.mozCancelFullScreen();
        } else if (doc.msExitFullscreen) {
          await doc.msExitFullscreen();
        }
        setIsFullscreen(false);
      } catch (err) {
        console.error('Error attempting to exit fullscreen:', err);
      }
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const doc = document as Document;
    const handleFullscreenChange = () => {
      setIsFullscreen(
        !!doc.fullscreenElement ||
        !!doc.webkitFullscreenElement ||
        !!doc.mozFullScreenElement ||
        !!doc.msFullscreenElement
      );
    };

    doc.addEventListener('fullscreenchange', handleFullscreenChange);
    doc.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    doc.addEventListener('mozfullscreenchange', handleFullscreenChange);
    doc.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      doc.removeEventListener('fullscreenchange', handleFullscreenChange);
      doc.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      doc.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      doc.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  // Handle mobile orientation change
  React.useEffect(() => {
    const handleOrientationChange = () => {
      if (window.orientation === 90 || window.orientation === -90) {
        // Landscape mode
        if (videoRef.current) {
          videoRef.current.style.width = '100%';
          videoRef.current.style.height = '100%';
        }
      } else {
        // Portrait mode
        if (videoRef.current) {
          videoRef.current.style.width = '100%';
          videoRef.current.style.height = 'auto';
        }
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  return (
    <>
      <CoverContainer ref={containerRef}>
        <VideoWrapper>
          <VideoBackground 
            ref={videoRef}
            autoPlay 
            loop 
            muted={isMuted}
            playsInline
          >
            <source src="https://cdn.thinkable.app/Thinkable-CBT-re-imagined.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </VideoBackground>
        </VideoWrapper>
        <TextOverlay isVisible={!isPlaying} />
        <ContentWrapper isVisible={!isPlaying}>
          <Title>{title}</Title>
          <Subtitle>{subtitle}</Subtitle>
          <CTAButton onClick={() => setIsDemoOpen(true)}>
            {ctaText}
          </CTAButton>
        </ContentWrapper>
        <VideoControls>
          <ControlButton onClick={togglePlay}>
            {isPlaying ? (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
                Pause
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Play
              </>
            )}
          </ControlButton>
          <ControlButton onClick={toggleMute}>
            {isMuted ? (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
                Unmute
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
                Mute
              </>
            )}
          </ControlButton>
          <ControlButton onClick={toggleFullscreen}>
            {isFullscreen ? (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
                </svg>
                Exit Fullscreen
              </>
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
                </svg>
                Fullscreen
              </>
            )}
          </ControlButton>
        </VideoControls>
      </CoverContainer>
      <BookDemo isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </>
  );
};

export default Cover; 